'use client';

import {
  IAPIError_getData,
  IAPIError_updateCount,
  IAvailablePosition,
  IAvailablePositionInclOrderType,
  IData,
  IOrderCountPerStatus,
} from 'my-poppies-b2b-types';
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { mockApiCallForData } from '@/api/mockApiCallForData';
import { useApi } from '@/api/useApi';
import { AuthContext } from '@/contexts/AuthContext';
import { LanguageContext } from '@/contexts/LanguageContext';
import { SettingsContext } from '@/contexts/SettingsContext';
import { dummyDataOrderCountPerStatus } from '@/dummyData/dummyDataOrderCountPerStatus';
import { IChangePriceModalData } from '@/types/IChangePriceModalData';
import { IDataContextProps } from '@/types/IDataContextProps';
import { IFetchState } from '@/types/IFetchState';
import { getIsDimmerShown } from '@/utils/getIsDimmerShown';
import { sortArrayByProperty } from '@/utils/sortArrayByProperty';

export const DataContext = createContext<IDataContextProps>({} as IDataContextProps);

export const DataContextProvider = ({ children }: PropsWithChildren) => {
  const { callApi } = useApi();
  const { userStatus } = useContext(AuthContext);
  const { settings, fetchSettingsState } = useContext(SettingsContext);
  const { currentLanguageWithFallback } = useContext(LanguageContext);

  const [data, setData] = useState<IData | undefined>(undefined);
  const [orderCountPerStatus, setOrderCountPerStatus] = useState<IOrderCountPerStatus | undefined>(undefined);
  const [fetchDataState, setFetchDataState] = useState<IFetchState>('FETCHING');
  const [changePriceModalData, setChangePriceModalData] = useState<IChangePriceModalData>(undefined);

  const fetchAndSetData = useCallback(async () => {
    if (!getIsDimmerShown()) {
      setFetchDataState('FETCHING');
    }

    try {
      const response = await callApi('get', '/data', {}, mockApiCallForData);

      if ((response as IAPIError_getData).errorCode) {
        throw new Error('Api Error');
      } else {
        setData(response as IData);
        setFetchDataState('READY');
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      setFetchDataState('ERROR');
    }
  }, [callApi]);

  const updateCount = useCallback(async () => {
    try {
      const response = await callApi('get', '/update-count', {}, () => {
        return dummyDataOrderCountPerStatus;
      });

      if ((response as IAPIError_updateCount).errorCode) {
        throw new Error('Api Error');
      } else {
        setOrderCountPerStatus(response as IOrderCountPerStatus);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      /// TODO #12345?
    }
  }, [callApi]);

  // if no user available, but some data is still there, we need to clean it:
  useEffect(() => {
    if (userStatus === 'NO_USER' && data) {
      setData(undefined);
    }
  }, [data, settings, userStatus]);

  // if user available, but no data yet, we need to fetch it:
  useEffect(() => {
    if (userStatus === 'USER_VALID' && fetchSettingsState === 'READY' && !data) {
      const updateData = async () => {
        await fetchAndSetData();
        await updateCount();
      };
      updateData();
    }
  }, [data, fetchAndSetData, fetchSettingsState, settings, updateCount, userStatus]);

  const availablePositionsForOrdersForCurrentUserRole: IAvailablePosition[] = useMemo(() => {
    if (!settings) {
      return [];
    }

    const availablePositionIdsForCurrentUserRole = data?.availablePositionIdsForOrders ?? [];

    return availablePositionIdsForCurrentUserRole
      .map((positionId) => data?.availablePositions?.find((ap) => ap.id === positionId) as IAvailablePosition)
      .filter(Boolean);
  }, [data?.availablePositionIdsForOrders, data?.availablePositions, settings]);

  const availablePositionsForPreordersForCurrentUserRole: IAvailablePosition[] = useMemo(() => {
    if (!settings) {
      return [];
    }

    const availablePositionIdsForPreordersForCurrentUserRole = data?.availablePositionIdsForPreorders ?? [];

    return availablePositionIdsForPreordersForCurrentUserRole
      .map((positionId) => data?.availablePositions?.find((ap) => ap.id === positionId) as IAvailablePosition)
      .filter(Boolean);
  }, [data?.availablePositionIdsForPreorders, data?.availablePositions, settings]);

  const allAvailablePositionsForCurrentUserRole: IAvailablePositionInclOrderType[] = useMemo(() => {
    return sortArrayByProperty<IAvailablePositionInclOrderType>(
      [
        ...(availablePositionsForOrdersForCurrentUserRole.map((p) => ({
          ...p,
          orderType: 'ORDER',
        })) as IAvailablePositionInclOrderType[]),
        ...(availablePositionsForPreordersForCurrentUserRole.map((p) => ({
          ...p,
          orderType: 'PREORDER',
        })) as IAvailablePositionInclOrderType[]),
      ],
      'createdAt'
    );
  }, [availablePositionsForOrdersForCurrentUserRole, availablePositionsForPreordersForCurrentUserRole]);

  const getProductTitleById = useCallback(
    (productId: string): string => {
      return data?.availablePositions?.find((ap) => ap.id === productId)?.title?.[currentLanguageWithFallback] ?? '';
    },
    [currentLanguageWithFallback, data?.availablePositions]
  );

  const value: IDataContextProps = useMemo((): IDataContextProps => {
    return {
      data,
      setData,
      fetchAndSetData,
      fetchDataState,
      updateCount,
      changePriceModalData,
      setChangePriceModalData,
      orderCountPerStatus,
      availablePositionsForOrdersForCurrentUserRole,
      availablePositionsForPreordersForCurrentUserRole,
      allAvailablePositionsForCurrentUserRole,
      getProductTitleById,
    };
  }, [
    data,
    fetchAndSetData,
    fetchDataState,
    updateCount,
    changePriceModalData,
    orderCountPerStatus,
    availablePositionsForOrdersForCurrentUserRole,
    availablePositionsForPreordersForCurrentUserRole,
    allAvailablePositionsForCurrentUserRole,
    getProductTitleById,
  ]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
